import { MageXPatient } from "@prisma/client";
import { JsonArray, JsonObject } from "@prisma/client/runtime/library";
import { Trans } from "@lingui/macro";
import { PatientSchema, TypeEnum } from "@api/src/types/few-schot-schema";
import { formatValue } from "@app/utils/format-field-value";
import { labelledValue } from "@app/utils/value-labels";
import ValueEditModal from "./ValueEditModal";
import { ValueCard } from "./ValueCard";
import { useState } from "react";

interface ValueDisplayProps {
  property: keyof MageXPatient;
  type?: TypeEnum;
  debug?: boolean;
  aggregatedValue:
    | string
    | number
    | boolean
    | JsonArray
    | Date
    | JsonObject
    | null
    | undefined;
  correctedValue?:
    | string
    | number
    | boolean
    | JsonArray
    | Date
    | JsonObject
    | null
    | undefined;
  onlyTruth?: boolean;
  schema: PatientSchema;
  label: string;
  hasValidatedTruth?: boolean;
  // Temporal projection page: render the value as a bordered "petit encart"
  // (see ValueCard) instead of a right-aligned inline span, so scalar, array
  // and empty fields all read uniformly. Off everywhere else.
  cardVariant?: boolean;
  setCorrectedValue: (
    value:
      | string
      | number
      | boolean
      | JsonArray
      | Date
      | JsonObject
      | null
      | undefined,
  ) => void;
}

export default function ValueDisplayNotArray({
  property,
  type = "string",
  debug = false,
  aggregatedValue,
  correctedValue,
  onlyTruth = false,
  schema,
  label,
  setCorrectedValue,
  hasValidatedTruth = false,
  cardVariant = false,
}: ValueDisplayProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // A term is checked against the list as stored, and SHOWN in the reader's language.
  const labels = schema[property]?.value_labels;
  if (onlyTruth) {
    const formattedTruthValue = formatValue(
      labelledValue(labels, correctedValue),
      property,
      type,
    );
    let isValid = true;
    const enumValues = schema[property]?.enum;
    if (enumValues?.length) {
      isValid = enumValues.includes(
        formatValue(correctedValue, property, type) as string,
      );
    }
    if (!isValid || isModalOpen) {
      return (
        <div className="flex justify-center items-center bg-black h-full">
          <button
            type="button"
            className="bg-black text-white flex w-full text-center flex-wrap overflow-hidden"
            onClick={() => setIsModalOpen(true)}
          >
            {formattedTruthValue}
          </button>

          <ValueEditModal
            open={isModalOpen}
            setIsOpen={setIsModalOpen}
            label={label}
            property={property}
            type={type}
            debug={debug}
            schema={schema}
            aggregatedValue={correctedValue}
            correctedValue={correctedValue}
            setCorrectedValue={setCorrectedValue}
            isNotStatued={true}
            calledFrom="ValueDisplayNotArray"
          />
        </div>
      );
    }

    return formattedTruthValue?.trim() ? (
      <span>{formattedTruthValue.trim()}</span>
    ) : (
      <span className="text-slate-400 italic"><Trans>Non déterminé</Trans></span>
    );
  }
  // Truth (correctedValue) wins over aggregated when set — a pencil-saved or
  // "Retenir"-validated value is the user's authoritative answer. Falls back to
  // the aggregated value only when no truth has been recorded for this field.
  // NOTE: boolean false is a valid value — do not treat it as missing.
  // When ``hasValidatedTruth`` is true, the user has explicitly validated this
  // field (possibly as empty), so an empty truth must NOT fall back to the
  // aggregated value — otherwise "Confirmer l'absence de valeur" still shows
  // the stale aggregated answer.
  const isCorrectedMissing =
    correctedValue == null ||
    correctedValue === "";

  const displayValue =
    hasValidatedTruth || !isCorrectedMissing ? correctedValue : aggregatedValue;

  const formattedOutputValue = formatValue(
    labelledValue(labels, displayValue),
    property,
    type,
    debug ? "valeur affichée" : undefined,
  );

  if (debug)
    console.warn({
      formattedOutputValue,
      displayValue,
      correctedValue,
      aggregatedValue,
    });

  const trimmed = formattedOutputValue?.trim();

  if (cardVariant) {
    return (
      <ValueCard muted={!trimmed}>
        {trimmed ? trimmed : <Trans>Non déterminé</Trans>}
      </ValueCard>
    );
  }

  return (
    <div className="relative group flex flex-col items-end gap-0.5">
      <span
        className={[
          "font-medium text-right block",
          !trimmed ? "text-slate-400 italic" : "text-slate-900",
        ].join(" ")}
      >
        {trimmed ? trimmed : <Trans>Non déterminé</Trans>}
      </span>
    </div>
  );
}
